import { useEffect, useRef } from 'react'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable, useSortable } from '@dnd-kit/react/sortable'
import { ChevronRight, GripVertical, Lock, Minus, Plus } from 'lucide-react'
import { WidgetCard, WidgetError, WidgetLoading, WidgetStat } from '@/components/dashboard/WidgetCard'
import { moduleStyle } from '@/components/dashboard/moduleStyle'
import type { ShiftStatus, ShiftTileSummary } from '@/api/shiftDashboardApi'
import { SHIFT_TILES, STATUS_META, moveTile, shiftTile, toggleTile } from '@/lib/shiftTiles'
import { cn } from '@/lib/utils'

/** A status as a word with its colour — never colour alone. */
export function StatusChip({ status, label, className }: { status: ShiftStatus; label?: string; className?: string }) {
  const meta = STATUS_META[status]
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        meta.chip,
        className,
      )}
    >
      <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
      {label ?? meta.label}
    </span>
  )
}

/**
 * Which app module each tile belongs to: its icon and colour come from that
 * module's Home tile (moduleStyle), exactly as the Home dashboard's widgets
 * do, and the header links to it.
 */
export const TILE_MODULE: Record<string, { moduleId: string; label: string; to: string }> = {
  procedures: { moduleId: 'upcoming-procedure', label: 'Procedures', to: '/procedures' },
  equipment: { moduleId: 'equipment', label: 'Equipment', to: '/equipment' },
  staff_on_shift: { moduleId: 'resource', label: 'Resources', to: '/resources' },
  who_is_out: { moduleId: 'resource', label: 'Resources', to: '/resources' },
  inventory: { moduleId: 'inventory', label: 'Inventory', to: '/inventory' },
  stock_shortages: { moduleId: 'pending-inventory', label: 'Inventory', to: '/inventory' },
  low_stock: { moduleId: 'storage', label: 'Reorder', to: '/inventory?tab=reorder' },
  product_custody: { moduleId: 'products', label: 'Products', to: '/products' },
  quality_safety: { moduleId: 'capa', label: 'CAPA', to: '/capa' },
  reviews_release: { moduleId: 'release', label: 'Reviews', to: '/my-steps?tab=reviews' },
  action_items: { moduleId: 'notifications', label: 'My steps', to: '/my-steps' },
  cleaning: { moduleId: 'clean', label: 'Cleaning', to: '/cleaning' },
}

/** The number's colour, as on Home: ink, unless the number is itself a problem. */
const STAT_TONE: Record<ShiftStatus, 'red' | 'amber' | 'blue'> = {
  critical: 'red',
  attention: 'amber',
  ready: 'blue',
  info: 'blue',
}

/**
 * The expanded grid (z8nrz7fbcn), in the Home dashboard's widget frame: the
 * module icon and colour, the title with its description one hover away, the
 * module link, a headline number with its status line, and the status as a
 * word. The number opens the tile's drill-down. A tile the server could not
 * compute shows why, in the same stated-state style Home widgets use.
 */
const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3'

export function ShiftTileGrid({
  ids,
  tiles,
  loading,
  onOpen,
  editing = false,
  onChange,
  onAdd,
}: {
  ids: string[]
  tiles: Map<string, ShiftTileSummary>
  loading: boolean
  onOpen: (id: string) => void
  /** Edit layout: drag to reorder, − to remove, a card to add. */
  editing?: boolean
  onChange?: (ids: string[]) => void
  onAdd?: () => void
}) {
  if (!editing || !onChange) {
    return (
      <ul className={GRID}>
        {ids.map((id) => (
          <li key={id}>
            <ShiftWidget id={id} tile={tiles.get(id)} loading={loading} onOpen={() => onOpen(id)} />
          </li>
        ))}
      </ul>
    )
  }

  // The same drag-and-drop the Home dashboard's editor uses.
  return (
    <DragDropProvider
      onDragEnd={(event) => {
        if (event.canceled) return
        const { source } = event.operation
        if (!isSortable(source)) return
        onChange(moveTile(ids, source.initialIndex, source.index - source.initialIndex))
      }}
    >
      <div className={GRID}>
        {ids.map((id, index) => (
          <EditableWidget
            key={id}
            id={id}
            index={index}
            tile={tiles.get(id)}
            loading={loading}
            onRemove={() => onChange(toggleTile(ids, id, false))}
          />
        ))}
        {onAdd && ids.length < SHIFT_TILES.length && (
          <button
            type='button'
            onClick={onAdd}
            className='flex min-h-[164px] flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/40 text-sm font-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          >
            <Plus aria-hidden className='h-5 w-5' />
            Add widget
            <span className='text-xs font-normal text-blue-600/80'>
              {SHIFT_TILES.length - ids.length} more available
            </span>
          </button>
        )}
      </div>
    </DragDropProvider>
  )
}

/** One tile in the Home dashboard's widget frame. */
function ShiftWidget({
  id,
  tile,
  loading,
  onOpen,
}: {
  id: string
  tile: ShiftTileSummary | undefined
  loading: boolean
  /** Absent while editing: the number does not open anything then. */
  onOpen?: () => void
}) {
  const def = shiftTile(id)
  const title = tile?.title ?? def?.title ?? id
  const mod = TILE_MODULE[id] ?? { moduleId: '', label: 'Open', to: '/shift-dashboard' }
  const stat = tile && (
    <WidgetStat
      value={tile.count.toLocaleString()}
      caption={tile.statusLine}
      tone={STAT_TONE[tile.status]}
      footer={
        <span className='flex items-center justify-between gap-2'>
          <StatusChip status={tile.status} />
          {onOpen && (
            <span className='flex items-center gap-0.5 text-xs font-medium text-gray-400 group-hover:text-blue-600'>
              Details <ChevronRight aria-hidden className='h-3.5 w-3.5' />
            </span>
          )}
        </span>
      }
    />
  )
  return (
    <WidgetCard
      title={title}
      description={def?.description}
      moduleId={mod.moduleId}
      moduleLabel={mod.label}
      moduleTo={mod.to}
      className='min-h-[164px]'
    >
      {loading && !tile ? (
        <WidgetLoading />
      ) : !tile ? (
        <WidgetError />
      ) : !tile.available ? (
        <div className='flex flex-1 flex-col items-center justify-center gap-1.5 py-4 text-center text-xs text-gray-400'>
          <Lock aria-hidden className='h-5 w-5' />
          {tile.statusLine}
        </div>
      ) : onOpen ? (
        <button
          type='button'
          onClick={onOpen}
          aria-label={`${title}: ${tile.count}, ${STATUS_META[tile.status].label}. Show details`}
          className='group -m-1 flex flex-1 flex-col rounded-lg p-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          {stat}
        </button>
      ) : (
        stat
      )}
    </WidgetCard>
  )
}

/** A tile while editing: handle to drag, − to remove, the live widget under a veil. */
function EditableWidget({
  id,
  index,
  tile,
  loading,
  onRemove,
}: {
  id: string
  index: number
  tile: ShiftTileSummary | undefined
  loading: boolean
  onRemove: () => void
}) {
  const { ref, handleRef, isDragging } = useSortable({ id, index })
  const title = tile?.title ?? shiftTile(id)?.title ?? id
  return (
    <div
      ref={ref}
      className={cn(
        'relative rounded-xl outline-dashed outline-2 outline-offset-2 outline-blue-300',
        isDragging && 'z-10 opacity-80 shadow-lg',
      )}
    >
      <div className='absolute inset-x-2 top-2 z-10 flex items-center justify-between gap-2'>
        <div
          ref={handleRef}
          tabIndex={0}
          aria-label={`${title}. Press Space to move`}
          aria-roledescription='movable widget'
          title='Drag to move'
          className={cn(
            'flex h-7 w-7 cursor-grab touch-none select-none items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
            isDragging && 'cursor-grabbing',
          )}
        >
          <GripVertical className='h-4 w-4' />
        </div>
        <button
          type='button'
          onClick={onRemove}
          aria-label={`Remove ${title}`}
          title={`Remove ${title}`}
          className='flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm hover:bg-red-50 hover:text-red-600'
        >
          <Minus className='h-4 w-4' />
        </button>
      </div>
      {/* Under a veil, as on Home: a click mid-edit never follows the module link. */}
      <div className='pt-8 [&_a]:pointer-events-none [&_section>div]:opacity-70'>
        <ShiftWidget id={id} tile={tile} loading={loading} />
      </div>
    </div>
  )
}

/**
 * The collapsed row (z8nrz7fbcn): once a tile is opened every tile shrinks
 * into one row that scrolls sideways, in the view's order, with the open tile
 * highlighted and kept in sight.
 */
export function ShiftTileStrip({
  ids,
  tiles,
  selected,
  onSelect,
}: {
  ids: string[]
  tiles: Map<string, ShiftTileSummary>
  selected: string
  onSelect: (id: string) => void
}) {
  const selectedRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    selectedRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduce ? 'auto' : 'smooth' })
  }, [selected])

  return (
    <div className='-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-2' role='tablist' aria-label='Shift tiles'>
      {ids.map((id) => {
        const tile = tiles.get(id)
        const isSelected = id === selected
        const title = tile?.title ?? shiftTile(id)?.title ?? id
        const style = moduleStyle(TILE_MODULE[id]?.moduleId ?? '')
        const Icon = style?.icon
        return (
          <button
            key={id}
            ref={isSelected ? selectedRef : undefined}
            type='button'
            role='tab'
            aria-selected={isSelected}
            disabled={!tile?.available}
            onClick={() => onSelect(id)}
            className={cn(
              'flex w-48 shrink-0 snap-start items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-shadow',
              isSelected
                ? 'border-blue-300 bg-blue-50 shadow-sm ring-1 ring-blue-200'
                : 'border-gray-200 bg-white shadow-sm hover:shadow-md',
              !tile?.available && 'cursor-not-allowed opacity-60',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            {Icon && (
              <span aria-hidden className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', style.tile)}>
                <Icon className='h-3.5 w-3.5 text-white' />
              </span>
            )}
            <span className='min-w-0 flex-1'>
              <span className='block truncate text-xs font-semibold text-gray-800'>{title}</span>
              {tile?.available && (
                <span className='mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500'>
                  <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', STATUS_META[tile.status].dot)} />
                  {STATUS_META[tile.status].label}
                </span>
              )}
            </span>
            <span
              className={cn(
                'text-lg font-bold tabular-nums',
                tile?.available && tile.status === 'critical' ? 'text-red-600' : tile?.status === 'attention' ? 'text-amber-600' : 'text-gray-900',
              )}
            >
              {tile?.available ? tile.count.toLocaleString() : '—'}
            </span>
          </button>
        )
      })}
    </div>
  )
}
